import { graphQLClient } from '@api/client';
import {
  PaymentMethod,
  PaymentStatusEnum,
  useRetryTreatmentPaymentActionMutation,
  useTreatmentHistoryDetailQuery
} from '@api/GoodocAPI';
import { useModalContext } from '@context/Modal';
import { useTreatmentInformationContext } from '@context/TreatmentInformation';
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import {
  RETRY_PAYMENT_METHOD_FIRST,
  RETRY_PAYMENT_METHOD_NEW,
  RETRY_PAYMENT_MODAL_CANCEL,
  RETRY_PAYMENT_MODAL_OK
} from '@pages/RetryPayment/focusKey';
import { PAYMENT } from '@router/path';
import constate from 'constate';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePaymentMethodsContext } from '../PaymentMethods';

const useRetryPayment = () => {
  const { paymentMethods } = usePaymentMethodsContext();
  const { selectedCreditCard, setSelectedCreditCard } = useTreatmentInformationContext();

  const { treatmentId = '' } = useParams();
  const { data: treatmentHistoryDetailResponse, refetch: refetchTreatmentHistoryDetail } =
    useTreatmentHistoryDetailQuery(graphQLClient, { treatmentId });

  const {
    isLoading: isLoadingRetryTreatmentPayment,
    mutateAsync: retryTreatmentPayment
  } = useRetryTreatmentPaymentActionMutation(graphQLClient);

  const lastPrescriptionPayment = useMemo(() => {
    if (treatmentHistoryDetailResponse?.treatmentV3.prescription?.prescriptionPayments) {
      const { prescriptionPayments } =
        treatmentHistoryDetailResponse?.treatmentV3.prescription ?? {};
      if (prescriptionPayments && prescriptionPayments.length > 0) {
        return prescriptionPayments[prescriptionPayments.length - 1];
      }
    }
    return null;
  }, [treatmentHistoryDetailResponse?.treatmentV3.prescription]);

  const lastDeliveryPayment = useMemo(() => {
    if (
      treatmentHistoryDetailResponse?.treatmentV3.prescription?.delivery?.deliveryPayments
    ) {
      const { deliveryPayments } =
        treatmentHistoryDetailResponse?.treatmentV3.prescription?.delivery ?? {};
      if (deliveryPayments && deliveryPayments.length > 0) {
        return deliveryPayments[deliveryPayments.length - 1];
      }
    }
    return null;
  }, [treatmentHistoryDetailResponse?.treatmentV3.prescription?.delivery]);

  const retryPayment = useCallback(async () => {
    try {
      if (selectedCreditCard && selectedCreditCard.id) {
        if (treatmentHistoryDetailResponse?.treatmentV3.paymentStatus === PaymentStatusEnum.PaymentFailed) {
          await retryTreatmentPayment({
            treatmentId,
            creditCardId: selectedCreditCard.id
          });
        }
      }
    } catch (e) {
      await refetchTreatmentHistoryDetail();
    }
  }, [treatmentHistoryDetailResponse?.treatmentV3.paymentStatus, refetchTreatmentHistoryDetail, retryTreatmentPayment, selectedCreditCard, treatmentId]);

  const loaderVisible = useMemo(() => {
    return isLoadingRetryTreatmentPayment;
  }, [isLoadingRetryTreatmentPayment]);

  const { visible, modalInfo, openModal, closeModal } = useModalContext();
  const openRetryPaymentModal = (paymentMethod: Partial<PaymentMethod>) => {
    setSelectedCreditCard(paymentMethod);

    openModal({
      title: '재결제',
      initialFocusKey: RETRY_PAYMENT_MODAL_OK,
      // TODO: set focus to previous focused button
      exitFocusKey: RETRY_PAYMENT_METHOD_FIRST,
      buttons: [
        {
          text: '취소',
          onClick: () => closeModal(),
          iconType: 'cancel',
          focusKey: RETRY_PAYMENT_MODAL_CANCEL
        },
        {
          text: '재결제하시겠습니까?',
          onClick: async () => {
            await retryPayment();
            closeModal();
            goBack();
          },
          iconType: 'arrow',
          focusKey: RETRY_PAYMENT_MODAL_OK
        }
      ]
    });
  };

  const { setFocus } = useFocusable();
  const setButtonFocus = useCallback(() => {
    if (visible) {
      setFocus(modalInfo.initialFocusKey);
    } else if (paymentMethods.length > 0) {
      setFocus(RETRY_PAYMENT_METHOD_FIRST);
    } else {
      setFocus(RETRY_PAYMENT_METHOD_NEW);
    }
  }, [modalInfo.initialFocusKey, paymentMethods, setFocus, visible]);

  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const goPaymentRegistration = () => {
    navigate(PAYMENT.REGISTRATION);
  };

  return {
    paymentMethods,
    lastPrescriptionPayment,
    lastDeliveryPayment,
    setButtonFocus,
    loaderVisible,
    openRetryPaymentModal,
    goPaymentRegistration
  };
};

export const [RetryPaymentProvider, useRetryPaymentContext] = constate(useRetryPayment);
